import {
  Autocomplete,
  AutocompleteRenderGetTagProps,
  Checkbox,
  Chip,
  createFilterOptions,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdClose,
} from '@teamworksdev/icons';
import {
  ComponentProps,
  HTMLAttributes,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../utils';
import {
  getOptionId,
  isSameOption,
  SelectableOption,
  withoutMissingOptions,
} from './multi-select-utils';

export type { SelectableOption };

function useElementWidth(ref: React.RefObject<HTMLElement>) {
  const [width, setWidth] = useState(0);
  const updateSize = (element: HTMLElement | null) => {
    if (element) {
      setWidth(element.clientWidth);
    }
  };

  useLayoutEffect(() => {
    const element = ref.current;
    const resizeObserver = new ResizeObserver(() => {
      updateSize(element);
    });
    if (element) {
      resizeObserver.observe(element);
    }
    updateSize(element);
    return () => {
      if (element) {
        resizeObserver.unobserve(element);
      }
    };
  }, [ref]);
  return width;
}

const SELECT_ALL_ID = 'SELECT_ALL';

export const MultiSelect = <T extends SelectableOption | string>({
  selectedOptions,
  limitTags,
  options,
  label,
  ariaLabel,
  describedById,
  placeholder,
  onChange,
  createOption,
  optionToName,
  fullWidth,
  disabled,
  showSelectAll = false,
  error = false,
}: {
  selectedOptions: T[];
  options: T[];
  limitTags?: number;
  label?: string;
  ariaLabel?: string;
  describedById?: string;
  placeholder?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  onChange: (selected: T[]) => void;
  createOption?: (name: string) => T | undefined;
  optionToName: (option: T) => string;
  showSelectAll?: boolean;
  error?: boolean;
}) => {
  const {
    palette: { info, greyscaleLight },
  } = useTheme();
  const { t } = useTranslation();

  // Create a special "Select All" option
  const SELECT_ALL_OPTION = useMemo(
    () =>
      ({
        id: SELECT_ALL_ID,
        name: t('selectAll'),
      }) as T,
    [t],
  );

  const isAllSelected =
    showSelectAll && selectedOptions.length === options.length;

  // Memoize the getId function to prevent recreation on every render
  const getId = useCallback((option: T | undefined) => getOptionId(option), []);

  // Options always exist, so the fallback is unreachable in practice. Keying by
  // id rather than MUI's default (the option label) keeps entries that share a
  // display name - duplicate exercise names are common in a team library -
  // from colliding on the same React key.
  const getOptionKey = useCallback(
    (option: T) => getOptionId(option) ?? '',
    [],
  );

  // Memoize the filter to prevent recreation on every render
  const filter = useMemo(() => createFilterOptions<T>(), []);

  const autocompleteRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(inputRef);
  const [resizeCompleted, setResizeCompleted] = useState(false);
  const [responsiveLimitTags, setResponsiveLimitTags] = useState(999999);

  // Memoize the resize effect to only run when width actually changes
  useEffect(() => {
    setResizeCompleted(false);
    setResponsiveLimitTags(999999);
    const resizeDebounce = setTimeout(() => {
      setResizeCompleted(true);
    }, 50);
    return () => {
      clearTimeout(resizeDebounce);
    };
  }, [width]);

  // Memoize the chip calculation effect to reduce unnecessary recalculations
  useEffect(() => {
    if (autocompleteRef.current && resizeCompleted) {
      const chips = autocompleteRef.current.querySelectorAll('.MuiChip-root');
      const input = autocompleteRef.current.querySelector('.MuiInputBase-root');
      if (input) {
        const computedStyle = getComputedStyle(input);
        const inputWidth =
          input.clientWidth -
          parseFloat(computedStyle.paddingLeft) -
          parseFloat(computedStyle.paddingRight) -
          5; // input has a min-width of 30px
        const chipGap = 4;
        const moreTextWidth = 20;
        const availableWidth = inputWidth - moreTextWidth;

        let totalChipWidth = 0;
        let indexToBreakAt = chips.length;

        for (let i = 0; i < chips.length; i++) {
          const currentChip = chips[i];
          const chipWidth = currentChip?.getBoundingClientRect().width ?? 0;
          const chipWithGap = i === 0 ? chipWidth : chipWidth + chipGap;

          if (totalChipWidth + chipWithGap > availableWidth) {
            indexToBreakAt = i;
            break;
          }

          totalChipWidth += chipWithGap;
        }

        if (chips.length > 0 && indexToBreakAt === 0) {
          indexToBreakAt = 1;
        }

        setResponsiveLimitTags((prev) =>
          prev !== indexToBreakAt ? indexToBreakAt : prev,
        );
      }
    }
  }, [selectedOptions, resizeCompleted]);

  // Memoize the final limit tags value
  const finalLimitTags = useMemo(
    () =>
      limitTags && limitTags < responsiveLimitTags
        ? limitTags
        : responsiveLimitTags,
    [limitTags, responsiveLimitTags],
  );

  // Memoize the filter options function
  const filterOptions = useCallback(
    (
      options: T[],
      state: { inputValue: string; getOptionLabel: (option: T) => string },
    ) => {
      const filtered: T[] = filter(options, state);

      const { inputValue } = state;
      const lowerInput = inputValue.toLocaleLowerCase();
      const isExisting = options.some((option) =>
        typeof option === 'string'
          ? lowerInput === option.toLocaleLowerCase()
          : lowerInput === option.name.toLocaleLowerCase(),
      );
      if (inputValue !== '' && !isExisting && createOption) {
        filtered.push({
          id: 'create',
          name: t('createNewTag', { tag: inputValue }),
        } as T);
      }

      return filtered;
    },
    [filter, createOption, t],
  );

  // Memoize the onChange handler
  const handleChange = useCallback(
    (_e: React.SyntheticEvent, rawValue: T[]) => {
      const value = withoutMissingOptions(rawValue);

      const selectAllClicked = value.some((v) => getId(v) === SELECT_ALL_ID);
      if (selectAllClicked && !isAllSelected) {
        onChange(options);
        return;
      } else if (selectAllClicked && isAllSelected) {
        onChange([]);
        return;
      }

      // Normal handling for regular options (filter out the Select All option)
      const filteredValue = value.filter((v) => getId(v) !== SELECT_ALL_ID);

      const selected = options.filter((opt) => {
        const id = getId(opt);
        return filteredValue.some((v) => {
          if (typeof v === 'string') {
            return id === v;
          } else {
            return id === v.id || id === v.name;
          }
        });
      });

      const newOptions = createOption
        ? filteredValue
            .filter((v) => typeof v !== 'string' && v.id === 'create')
            .flatMap((v) => {
              const match = (v as SelectableOption).name.match(
                /Create new tag '(.+)'/,
              );
              const inputValue = match?.[1] ?? (v as SelectableOption).name;
              const newOption = createOption(inputValue);
              return newOption ? [newOption] : [];
            })
        : [];

      onChange([...selected, ...newOptions]);
    },
    [options, getId, createOption, onChange, selectedOptions],
  );

  // Memoize the isOptionEqualToValue function
  const isOptionEqualToValue = useCallback(
    (opt: T, val: T) => isSameOption(opt, val),
    [],
  );

  // Memoize the getOptionLabel function
  const getOptionLabel = useCallback(
    (option: T) => optionToName(option),
    [optionToName],
  );

  // Memoize the renderOption function
  const renderOption = useCallback(
    (
      props: HTMLAttributes<HTMLLIElement> & { key: string },
      option: T,
      { selected }: { selected: boolean },
    ) => {
      // `key` has to be pulled out and passed explicitly rather than spread.
      // MUI BUG: https://github.com/mui/material-ui/issues/42161
      const { key, ...rest } = props;
      const { className, ...optionProps } = rest;

      const isSelectAllOption = getId(option) === SELECT_ALL_ID;
      let isNotNewOption = true;
      let label;
      if (typeof option === 'string') {
        label = option;
      } else {
        label = getOptionLabel(option);
        isNotNewOption = option.id !== 'create';
      }

      return (
        <li
          key={key}
          {...optionProps}
          className={cn(
            'p-0 m-0 rounded h-9 pl-1 flex items-center',
            isSelectAllOption &&
              'sticky top-0 z-10 bg-white border-b font-medium',
            className,
          )}
        >
          {isNotNewOption && (
            <Checkbox
              icon={<MdCheckBoxOutlineBlank size={20} />}
              checkedIcon={<MdCheckBox size={20} color={info.main} />}
              checked={isSelectAllOption ? isAllSelected : selected}
              sx={{
                padding: 0,
              }}
            />
          )}
          <span className="ml-2 whitespace-nowrap">{label}</span>
        </li>
      );
    },
    [info.main, greyscaleLight, isAllSelected, getId],
  );

  // Memoize the renderInput function
  const renderInput = useCallback(
    (params: ComponentProps<typeof TextField>) => (
      <TextField
        ref={inputRef}
        {...params}
        label={label}
        placeholder={selectedOptions.length === 0 ? placeholder : ''}
        error={error}
        inputProps={{
          ...params.inputProps,
          ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
          ...(describedById ? { 'aria-describedby': describedById } : {}),
        }}
      />
    ),
    [
      label,
      placeholder,
      selectedOptions.length,
      error,
      ariaLabel,
      describedById,
    ],
  );

  // Memoize the renderTags function
  const renderTags = useCallback(
    (value: T[], getTagProps: AutocompleteRenderGetTagProps) =>
      value.map((option, index) => {
        const { key, ...tagProps } = getTagProps({ index });
        return (
          <Chip
            key={key}
            {...tagProps}
            label={optionToName(option)}
            sx={{
              paddingY: '3px',
              paddingX: '4px',
              height: '20px',
              fontSize: '11px',
              borderRadius: '3px',
              fontWeight: 500,
              border: `1px solid ${greyscaleLight[200]}`,
              backgroundColor: greyscaleLight[50],
              '& .MuiChip-label': {
                padding: '0px 4px 0px 0px',
              },
              '& .MuiChip-deleteIcon': {
                margin: 0,
              },
            }}
            deleteIcon={<MdClose size={14} aria-hidden="true" />}
          />
        );
      }),
    [optionToName, greyscaleLight],
  );

  // Memoize the getLimitTagsText function
  const getLimitTagsText = useCallback(
    (more: number) => (
      <Typography variant="subtitle1Medium">{`+${more}`}</Typography>
    ),
    [],
  );
  return (
    <Autocomplete
      ref={autocompleteRef}
      multiple
      options={showSelectAll ? [SELECT_ALL_OPTION, ...options] : options}
      value={selectedOptions}
      disableCloseOnSelect
      forcePopupIcon
      disabled={disabled}
      limitTags={finalLimitTags}
      fullWidth={fullWidth}
      includeInputInList={false}
      getOptionKey={getOptionKey}
      getOptionLabel={getOptionLabel}
      openOnFocus={true}
      getLimitTagsText={getLimitTagsText}
      filterOptions={filterOptions}
      onChange={handleChange}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={renderOption}
      renderInput={renderInput}
      sx={{
        '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
          minWidth: 0,
        },
      }}
      ListboxProps={{
        sx: {
          maxHeight: '200px',
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          '& .MuiAutocomplete-option': {
            padding: '8px 12px',
            minHeight: '36px',
            maxHeight: '60px',
          },
        },
      }}
      renderTags={renderTags}
      componentsProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
          ],
        },
      }}
    />
  );
};
